"use client";

import { useEffect, useMemo } from "react";

import { ModalType } from "entities/common";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal } from "store/slices/modalSlice";

import WidgetModalCreateAsset from "./CreateAsset";
import WidgetModalCreateCompany from "./CreateCompany";
import WidgetModalCreateDevice from "./CreateDevice";
import WidgetModalCreateGateway from "./CreateGateway";
import WidgetModalCreatePortfolio from "./CreatePortfolio";
import WidgetModalGatewayConfigInstructions from "./GatewayConfigInstructions";
import WidgetModalGenerateReport from "./GenerateReport";
import WidgetModalMarkCalibrated from "./MarkCalibrated";
import WidgetModalNotice from "./Notice";
import WidgetModalRemoveAsset from "./RemoveAsset";
import WidgetModalRemoveCompany from "./RemoveCompany";
import WidgetModalRemoveDevice from "./RemoveDevice";
import WidgetModalRemoveGateway from "./RemoveGateway";
import WidgetModalRemovePortfolio from "./RemovePortfolio";
import WidgetModalRemoveReport from "./RemoveReport";
import WidgetModalRemoveUser from "./RemoveUser";
import WidgetModalUser from "./UserModal";
import WidgetModalWhatsAppVerification from "./WhatsAppVerification";

const WidgetModal = () => {
  const dispatch = useAppDispatch();
  const { modalType, modalProps, active } = useAppSelector((state: RootStore) => state.modal);

  useEffect(() => {
    const handlePopState = () => {
      if (active) {
        dispatch(hideModal());
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [active, dispatch]);

  const noticeProps = useMemo(() => {
    const { callbackKey, ...rest } = (modalProps || {}) as Record<string, unknown> & {
      callbackKey?: string;
    };
    if (callbackKey) {
      const callbacks = getModalCallbacks(callbackKey);
      return {
        ...rest,
        onClick: callbacks?.onClick
          ? () => {
              callbacks.onClick?.();
              dispatch(hideModal());
            }
          : undefined,
        onClick2: callbacks?.onClick2
          ? () => {
              callbacks.onClick2?.();
              dispatch(hideModal());
            }
          : undefined,
      };
    }
    return rest;
  }, [modalProps, dispatch]);

  const renderModal = () => {
    switch (modalType) {
      case ModalType.NOTICE:
        return <WidgetModalNotice isVisible={active} {...noticeProps} />;
      case ModalType.REMOVE_COMPANY:
        return <WidgetModalRemoveCompany isVisible={active} {...modalProps} />;
      case ModalType.CREATE_COMPANY:
        return <WidgetModalCreateCompany isVisible={active} {...modalProps} />;
      case ModalType.CREATE_PORTFOLIO:
        return <WidgetModalCreatePortfolio isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_PORTFOLIO:
        return <WidgetModalRemovePortfolio isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_REPORT:
        return <WidgetModalRemoveReport isVisible={active} {...modalProps} />;
      case ModalType.USER:
        return <WidgetModalUser isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_USER:
        return <WidgetModalRemoveUser isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_DEVICE:
        return <WidgetModalRemoveDevice isVisible={active} {...modalProps} />;
      case ModalType.CREATE_ASSET:
        return <WidgetModalCreateAsset isVisible={active} {...modalProps} />;
      case ModalType.CREATE_GATEWAY:
        return <WidgetModalCreateGateway isVisible={active} {...modalProps} />;
      case ModalType.CREATE_DEVICE:
        return <WidgetModalCreateDevice isVisible={active} {...modalProps} />;
      case ModalType.GENERATE_REPORT:
        return <WidgetModalGenerateReport isVisible={active} {...modalProps} />;
      case ModalType.MARK_CALIBRATED:
        return <WidgetModalMarkCalibrated isVisible={active} {...modalProps} />;
      case ModalType.GATEWAY_CONFIG_INSTRUCTIONS:
        return <WidgetModalGatewayConfigInstructions isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_ASSET:
        return <WidgetModalRemoveAsset isVisible={active} {...modalProps} />;
      case ModalType.REMOVE_GATEWAY:
        return <WidgetModalRemoveGateway isVisible={active} {...modalProps} />;
      case ModalType.WHATSAPP_VERIFICATION:
        return <WidgetModalWhatsAppVerification isVisible={active} {...modalProps} />;
      default:
        return null;
    }
  };

  return renderModal();
};

export default WidgetModal;
